import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'

import SkeletonTable from './SkeletonTable'

export type SkeletonSearchProps = {
    rows?: number
    columns?: number
    showTabs?: boolean
    fill?: boolean | number | string
}

const fillHeight = (fill: boolean | number | string) => {
    if (fill === false) return undefined
    if (fill === true) return 'calc(100vh - 260px)'
    return fill
}

export default function SkeletonSearch(props: Readonly<SkeletonSearchProps>) {
    const { rows = 12, columns = 5, showTabs = false, fill = true } = props

    return (
        <Stack spacing={2} sx={{ width: '100%' }}>
            {showTabs && (
                <Stack
                    direction="row"
                    spacing={3}
                    sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                    <Skeleton variant="text" width={90} />
                    <Skeleton variant="text" width={120} />
                </Stack>
            )}

            <Stack
                direction="row"
                spacing={2}
                sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 2 }}
            >
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="rounded" height={32} sx={{ flexGrow: 1, minWidth: 220 }} />
                <Skeleton variant="rounded" width={90} height={24} />
                <Skeleton variant="rounded" width={240} height={40} />
                <Skeleton variant="rounded" width={240} height={40} />
            </Stack>

            <Skeleton variant="rounded" height={44} />

            <Paper
                variant="outlined"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: fillHeight(fill),
                    overflow: 'hidden',
                }}
            >
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                        px: 2,
                        py: 1.5,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Skeleton variant="text" width={180} sx={{ fontSize: '1.25rem' }} />
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                        <Skeleton variant="text" width={90} />
                        {Array.from({ length: 4 }, (_, index) => (
                            <Skeleton key={index} variant="circular" width={22} height={22} />
                        ))}
                        <Box sx={{ width: 8 }} />
                        {Array.from({ length: 4 }, (_, index) => (
                            <Skeleton key={index} variant="circular" width={22} height={22} />
                        ))}
                    </Stack>
                </Stack>

                <Box sx={{ flexGrow: 1, px: 0 }}>
                    <SkeletonTable rows={rows} columns={columns} showToolbar={false} />
                </Box>

                <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{ px: 2, py: 1.5, alignItems: 'center', justifyContent: 'flex-end' }}
                >
                    <Skeleton variant="text" width={90} />
                    {Array.from({ length: 4 }, (_, index) => (
                        <Skeleton key={index} variant="circular" width={22} height={22} />
                    ))}
                </Stack>
            </Paper>
        </Stack>
    )
}
